import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import { useTheme } from '@mui/material/styles';
import api from './api';
import { useUser } from './UserContext';
import BusyOverlay from './BusyOverlay';

const INVITE_CODE_KEY = 'pendingInviteCode';

const JoinClub = () => {
  const { code: codeParam } = useParams();
  const [searchParams] = useSearchParams();
  const code = codeParam || searchParams.get('inviteCode') || '';
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useUser();

  const [clubInfo, setClubInfo] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`/invite/${encodeURIComponent(code)}`);
        setClubInfo(res.data.result);
      } catch (err) {
        if (err.response?.status === 410) {
          setLoadError('This invite link has expired.');
        } else if (err.response?.status === 404) {
          setLoadError('This invite link is not valid.');
        } else {
          setLoadError('Could not load invite information.');
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [code]);

  // If user just logged in and there's a pending invite code, auto-accept it
  const handleAccept = useCallback(async () => {
    setIsAccepting(true);
    setAcceptError('');
    try {
      const res = await api.post(`/invite/${encodeURIComponent(code)}/accept`);
      const { clubShortName, activeContests } = res.data.result;
      sessionStorage.removeItem(INVITE_CODE_KEY);
      if (activeContests?.length === 1) {
        navigate(`/club/${clubShortName}/contest/${activeContests[0].contestShortName}`);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setIsAccepting(false);
      if (err.response?.status === 410) {
        setAcceptError('This invite link has expired.');
      } else if (err.response?.status === 404) {
        setAcceptError('This invite link is no longer valid.');
      } else {
        setAcceptError(err.response?.data || 'Failed to join club. Please try again.');
      }
    }
  }, [code, navigate]);

  const handleLoginWithInvite = () => {
    sessionStorage.setItem(INVITE_CODE_KEY, code);
    navigate('/login');
  };

  const handleSignUpWithInvite = () => {
    sessionStorage.setItem(INVITE_CODE_KEY, code);
    navigate('/signup');
  };

  const handleGoogleWithInvite = () => {
    sessionStorage.setItem(INVITE_CODE_KEY, code);
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`;
  };

  const handleAppleWithInvite = () => {
    sessionStorage.setItem(INVITE_CODE_KEY, code);
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/apple`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <BusyOverlay open={isAccepting} message="Joining club..." />
      <Box sx={{
        marginBottom: 8,
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            maxWidth: 500,
            width: '90%',
            textAlign: 'center',
            backgroundColor: theme.palette.background,
            color: theme.palette.text.primary,
          }}
        >
          {loadError ? (
            <>
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
                Invite Link Invalid
              </Typography>
              <Typography variant="body1" color="error" sx={{ mb: 3 }}>
                {loadError}
              </Typography>
              <Link to="/login" style={{ color: theme.palette.primary.main }}>
                Go to Login
              </Link>
            </>
          ) : (
            <>
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>
                You're Invited!
              </Typography>
              <Typography variant="body1" sx={{ mb: 0.5 }}>
                <strong>{clubInfo.ownerName}</strong> has invited you to join
              </Typography>
              <Typography variant="h6" sx={{ mb: 3 }}>
                {clubInfo.clubName}
              </Typography>

              {acceptError && (
                <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                  {acceptError}
                </Typography>
              )}

              {user ? (
                // Logged-in user can accept directly
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  onClick={handleAccept}
                  style={{ color: theme.palette.primary.contrastText }}
                >
                  Join {clubInfo.clubName}
                </Button>
              ) : (
                // Not logged in — offer login/signup options
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sign in or create an account to join this club.
                  </Typography>

                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleGoogleWithInvite}
                    startIcon={
                      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                    }
                    sx={{
                      mb: 1,
                      backgroundColor: '#fff',
                      color: '#3c4043',
                      borderColor: '#dadce0',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.9375rem',
                      py: 1,
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        borderColor: '#dadce0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                      },
                    }}
                  >
                    Continue with Google
                  </Button>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleAppleWithInvite}
                    startIcon={
                      <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path fill="currentColor" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                      </svg>
                    }
                    sx={{
                      mt: 1,
                      backgroundColor: '#000',
                      color: '#fff',
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.9375rem',
                      py: 1,
                      '&:hover': {
                        backgroundColor: '#1a1a1a',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      },
                    }}
                  >
                    Continue with Apple
                  </Button>

                  <Box sx={{ display: 'flex', alignItems: 'center', my: 1.5 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>or</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={handleLoginWithInvite}
                    >
                      Log In
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSignUpWithInvite}
                      style={{ color: theme.palette.primary.contrastText }}
                    >
                      Sign Up
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </Paper>
      </Box>
    </>
  );
};

export { INVITE_CODE_KEY };
export default JoinClub;
